/**
 * Server-side logs reach Coralogix through the cluster's OTel agent, which ships
 * container stdout and stderr into prod/store and prod/portal. That needs no SDK,
 * key or startup step — which is exactly what the store's previous logger
 * required and never had, leaving it with no server-side telemetry at all.
 *
 * Shared because both Next servers land in the same collector and should be
 * queryable the same way.
 */
export type ServerLogLevel = 'debug' | 'info' | 'warning' | 'error' | 'critical';

export interface ServerLogger<TEvent extends string> {
    debug(event: TEvent, data?: Record<string, unknown>): void;
    info(event: TEvent, data?: Record<string, unknown>): void;
    warn(event: TEvent, data?: Record<string, unknown>): void;
    error(event: TEvent, data?: Record<string, unknown>): void;
    critical(event: TEvent, data?: Record<string, unknown>): void;
}

/** Typed per app so each keeps its own event vocabulary. */
export const createServerLogger = <TEvent extends string>(): ServerLogger<TEvent> => ({
    debug: (event, data) => write('debug', event, data),
    info: (event, data) => write('info', event, data),
    warn: (event, data) => write('warning', event, data),
    error: (event, data) => write('error', event, data),
    critical: (event, data) => write('critical', event, data),
});

/**
 * One line per record, always. The collector splits on newlines, so logging an
 * object directly turns a single event into dozens of contextless records — the
 * portal's boot log was doing that and accounted for 96% of its log volume.
 *
 * Severity is derived from the stream, so the levels must not all collapse onto
 * console.log or a failure gets indexed as Info.
 */
const write = (level: ServerLogLevel, event: string, data: Record<string, unknown> = {}): void => {
    const line = serializeLine({ ...data, event, level });

    if (level === 'debug' || level === 'info') {
        console.log(line);
        return;
    }
    if (level === 'warning') {
        console.warn(line);
        return;
    }
    console.error(line);
};

const serializeLine = (payload: Record<string, unknown>): string => {
    try {
        return JSON.stringify(payload, serializeErrors);
    } catch {
        // A circular or unserializable value must never break the request that
        // was only trying to describe itself.
        return JSON.stringify({
            event: payload.event,
            level: payload.level,
            serializationFailed: true,
        });
    }
};

/**
 * JSON.stringify renders an Error as {}, and callers pass them constantly, so
 * without this the only thing recorded about a failure is that it happened.
 */
const serializeErrors = (_key: string, value: unknown): unknown =>
    value instanceof Error
        ? { name: value.name, message: value.message, stack: value.stack }
        : value;
